import type { FC, ReactNode } from "react";
import { usePress } from "react-aria";
import { twJoin } from "tailwind-merge";

export interface InteractiveQuoteProps {
  className: string;
  metadataClassName?: string;
  onPress: () => void;
  quote: string;
  /** Optional transcript position displayed as compact metadata in the chip. */
  timestamp?: string;
  children: ReactNode;
}

/**
 * A quote that acts in place, such as focusing the open transcript. It is a
 * span with the button role because a `<button>` lays out as an atomic box, so
 * a long quote would stop wrapping with the sentence around it.
 */
export const InteractiveQuote: FC<InteractiveQuoteProps> = (props) => {
  const { className, metadataClassName, onPress, quote, timestamp, children } =
    props;
  const { pressProps } = usePress({ onPress });
  return (
    <span
      {...pressProps}
      aria-label={timestamp ? `${quote}, ${timestamp}` : undefined}
      className={twJoin(className, "cursor-pointer")}
      role="button"
      tabIndex={0}
    >
      {/* Keep the hitbox between lines larger than the chip and its focus ring. */}
      <span className="box-decoration-clone py-[0.375lh]">
        {children}
        {timestamp ? (
          <span
            className={twJoin(
              "ml-2 text-[0.6875rem] leading-none font-normal",
              metadataClassName,
            )}
          >
            {timestamp}
          </span>
        ) : null}
      </span>
    </span>
  );
};
